import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import {
    Box,
    Heading,
    Paragraph,
    ResponsiveContext,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
    Text,
} from 'grommet';
import { useContext } from 'react';

import { CollectionImageGallery } from 'components/shared/CollectionImageGallery';
import causesSupported from 'content/causes-supported.json';

dayjs.extend(localizedFormat);

interface CausesSupportedEntry {
    name: string;
    // Rendered through `dayjs(date).format('L')`, so unlike Money Raised's
    // eventDate this must actually parse. 'Q1 2026' renders 'Invalid Date'.
    date: string;
    // Dollars, not cents. Required — a cause with no amount cannot be added.
    amount: number;
    use: string;
}

// The annotation is the check: a malformed row in the JSON is a build failure
// here rather than a blank cell on the site.
const CausesSupportedData: CausesSupportedEntry[] = causesSupported;

const Columns: {
    label: string;
    property: keyof CausesSupportedEntry;
    format?: (data: CausesSupportedEntry) => string | undefined;
}[] = [
    { label: 'Cause', property: 'name' },
    {
        label: 'Date',
        property: 'date',
        format: (data) => (data.date ? dayjs(data.date).format('L') : undefined),
    },
    {
        label: 'Amount',
        property: 'amount',
        format: (data) =>
            data.amount?.toLocaleString(undefined, {
                style: 'currency',
                currency: 'USD',
            }),
    },
];

export const CausesSupported = () => {
    const size = useContext(ResponsiveContext);
    const isMobile = size === 'small';
    return (
        <Box alignSelf="center" width="xxlarge" pad={{ bottom: 'medium' }}>
            <Heading level={2}>CAUSES SUPPORTED</Heading>
            <Box direction={!isMobile ? 'row' : 'column'} gap="medium">
                <Box>
                    <Heading level={3}>Contributions</Heading>
                    <Table margin={{ bottom: 'small' }}>
                        <TableHeader>
                            <TableRow>
                                {Columns.map((c) => (
                                    <TableCell
                                        key={c.property}
                                        scope="col"
                                        border={{ color: 'gray', side: 'bottom' }}
                                    >
                                        <Text weight="bold">{c.label}</Text>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {CausesSupportedData.map((cause, i) => (
                                <TableRow key={`${cause.name}${cause.date}`}>
                                    {Columns.map((c, j) => (
                                        <TableCell
                                            key={c.property}
                                            scope="col"
                                            border={
                                                i !== CausesSupportedData.length - 1
                                                    ? { color: 'gray', side: 'bottom' }
                                                    : undefined
                                            }
                                        >
                                            <Text>{c.format?.(cause) ?? cause[c.property]}</Text>
                                            {j === 0 && (
                                                <Box>
                                                    <Text>{cause.use}</Text>
                                                </Box>
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <CollectionImageGallery name="causes-supported" />
                </Box>
                <Box
                    width={!isMobile ? '30%' : undefined}
                    background="gray"
                    pad={{ left: 'medium', right: 'medium' }}
                >
                    <Heading level={3}>Where The Money Goes</Heading>
                    <Text weight="bold">Children&apos;s Hospital Pittsburgh</Text>
                    <Paragraph fill>
                        Chelsea was treated at CHP from June 2007 until she passed July 2019. She
                        spent countless hours in the Hematology Oncology Clinic, along with other
                        departments such as radiation (for MRIs), and doing therapies and testing.
                        The goal of the foundation is to work with the HemOnc clinic to determine
                        what would be a best fit items/services to donate.
                    </Paragraph>
                    <Text weight="bold">Make-a-Wish</Text>
                    <Paragraph fill>
                        Chelsea was granted her wish of a bedroom makeover, the fall of 2007. The
                        foundation will donate the funds to grant wishes for other children.
                    </Paragraph>
                    <Text weight="bold">St. Jude Children&apos;s Research Hospital</Text>
                    <Paragraph fill>
                        Chelsea lived at St. Jude with her mom from July 2018 - February 2019.
                        During this time, she received all medical care and housing free of charge.
                        The family was also given food/necessities allowance each week. In addition
                        to basic needs, St. Jude offered numerous events sponsored by many different
                        Companies and Organizations. The goal of the foundation is to sponsor an
                        event or events, geared towards the young adults being treated there.
                    </Paragraph>
                    <Text weight="bold">Scholarship for graduating senior</Text>
                    <Paragraph fill>
                        The foundation will sponsor at least one scholarship per academic year to a
                        High School Senior, with at least one of the following criteria: Plan to
                        attend Indiana University of Pennsylvania (IUP), Plan to major/minor in
                        Interior Design or Theater, Brain cancer diagnosis, or the need for
                        Disability Support Services (DSS).
                    </Paragraph>
                    <Text weight="bold">Brain Cancer Research</Text>
                    <Paragraph fill>
                        The foundation will donate to a hospital or other organization that is
                        actively doing research to help find a cure for pediatric brain cancer.
                        Focus will be on research for Medulloblastoma when possible.
                    </Paragraph>
                </Box>
            </Box>
        </Box>
    );
};
